import type { SellerUnitAndProject, SellerUnitsAndProjectsOne } from "@/types/sup";
import type { BreadcrumbItem } from "@/utils/breadcrumb-utils";

export const buildSupOrProjectBreadcrumbItems = ({
	city,
	slug,
	supOne,
	projectDetails,
}: {
	city?: string;
	slug?: string;
	supOne?: SellerUnitAndProject | SellerUnitsAndProjectsOne;
	projectDetails?: { projectName: string; projectId?: string };
}): BreadcrumbItem[] => {
	const items: BreadcrumbItem[] = [];

	let cityName = city;
	if (!cityName && supOne?.address) {
		if (supOne.address.includes("Bengaluru") || supOne.address.includes("Bangalore")) {
			cityName = "bengaluru";
		}
	}
	cityName = cityName || "bengaluru";

	items.push({ label: "Home", variant: "default", href: "/" });
	items.push({
		label: cityName.charAt(0).toUpperCase() + cityName.slice(1).toLowerCase(),
		variant: "city",
		href: `/${cityName.toLowerCase()}`,
	});

	if (!supOne && projectDetails) {
		items.push({ label: projectDetails.projectName, variant: "locality", isActive: true });
		return items;
	}
	if (!supOne) return items;

	let locality = supOne.location?.locality;
	if (!locality && supOne.address) {
		const addressParts = supOne.address.split(",").map(p => p.trim());
		locality = addressParts.find(
			part =>
				part &&
				!part.includes("Karnataka") &&
				!part.includes("Bengaluru") &&
				!part.includes("Bangalore") &&
				part.length > 3 &&
				!/^[\d]/.test(part),
		);
	}

	const isStandaloneUnit =
		supOne.type === "unit" &&
		(supOne.propertyTypes?.[0]?.toLowerCase().includes("house") ||
			supOne.propertyTypes?.[0]?.toLowerCase().includes("villa") ||
			supOne.propertyTypes?.[0]?.toLowerCase().includes("independent"));

	const addBedroomFromSlug = () => {
		const m = slug?.match(/(\d+bhk)/i);
		if (m) items.push({ label: m[1].toUpperCase(), variant: "bedroom", isActive: true });
	};

	if (isStandaloneUnit) {
		if (locality) {
			const localitySlug = locality
				.toLowerCase()
				.replace(/\s+/g, "_")
				.replace(/[^a-z0-9_]/g, "");
			items.push({
				label: locality,
				variant: "locality",
				href: `/${cityName.toLowerCase()}/in-${localitySlug}`,
			});
		}
		const propertyType = supOne.propertyTypes?.[0];
		if (propertyType && locality) {
			const localitySlug = locality
				.toLowerCase()
				.replace(/\s+/g, "_")
				.replace(/[^a-z0-9_]/g, "");
			const propertyTypeSlug = propertyType.toLowerCase();
			items.push({
				label: propertyType.charAt(0).toUpperCase() + propertyType.slice(1).toLowerCase(),
				variant: "propertyType",
				href: `/${cityName.toLowerCase()}/${propertyTypeSlug}-in-${localitySlug}`,
			});
		} else if (propertyType) {
			const propertyTypeSlug = propertyType.toLowerCase();
			items.push({
				label: propertyType.charAt(0).toUpperCase() + propertyType.slice(1).toLowerCase(),
				variant: "propertyType",
				href: `/${cityName.toLowerCase()}?propertyType=${propertyTypeSlug}`,
			});
		}
		addBedroomFromSlug();
	} else if (supOne.projectId && supOne.projectName) {
		const projectSlug = supOne.projectName
			.toLowerCase()
			.replace(/\s+/g, "_")
			.replace(/[^a-z0-9_]/g, "");
		items.push({
			label: supOne.projectName,
			variant: "locality",
			href: `/projects/${projectSlug ? projectSlug + "-" : ""}${supOne.projectId}`,
		});
		addBedroomFromSlug();
	} else {
		if (locality) {
			const localitySlug = locality
				.toLowerCase()
				.replace(/\s+/g, "_")
				.replace(/[^a-z0-9_]/g, "");
			items.push({
				label: locality,
				variant: "locality",
				href: `/${cityName.toLowerCase()}/in-${localitySlug}`,
			});
		}
		const propertyType = supOne.propertyTypes?.[0];
		if (propertyType && locality) {
			const localitySlug = locality
				.toLowerCase()
				.replace(/\s+/g, "_")
				.replace(/[^a-z0-9_]/g, "");
			const propertyTypeSlug = propertyType.toLowerCase();
			items.push({
				label: propertyType.charAt(0).toUpperCase() + propertyType.slice(1).toLowerCase(),
				variant: "propertyType",
				href: `/${cityName.toLowerCase()}/${propertyTypeSlug}-in-${localitySlug}`,
			});
		} else if (propertyType) {
			const propertyTypeSlug = propertyType.toLowerCase();
			items.push({
				label: propertyType.charAt(0).toUpperCase() + propertyType.slice(1).toLowerCase(),
				variant: "propertyType",
				href: `/${cityName.toLowerCase()}?propertyType=${propertyTypeSlug}`,
			});
		}
		addBedroomFromSlug();
	}

	return items;
};
